import { Button, Flex, usePermission } from 'portal-commons';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { faArrowProgress } from '@fortawesome/pro-regular-svg-icons';
import { Grid } from '@material-ui/core';

import CNPDetails from '../CNPDetails';
import CancelMigrationModal from '../CancelMigrationModal';
import ElectCNPDialog from '../ElectCNPDialog';
import InitiateMigrationModal from '../InitiateMigrationModal';
import ReSellerDetails from '../ReSellerDetails';
import SectionHeader from '../SectionHeader';
import {
  CampaignActionEligibility,
  CampaignDetail,
  CampaignSharingStatus,
  CampaignStatus,
  Reseller,
  cnpChainController,
} from '@/apis/core';
import { SelectItem } from '@/utils/types';

interface OtherResponsiblePartiesSectionProps {
  variant?: 'csp' | 'cnp';
  loading?: boolean;
  provisional?: boolean | null;
  campaign: CampaignDetail;
  nominationInfo: {
    status?: string;
    upstreamCnpName?: string;
    upstreamCnpUid?: string;
  };
  electCnpOptions: SelectItem[];
  migrationCnpOptions?: SelectItem[];
  reseller?: Reseller;
  onCnpChange: (success: boolean) => void;
  onMigrationApply?: (upstreamCnpUid: string) => void;
  onMigrationCancel?: (explanation: string) => void;
  onResellerChange?: (resellerUid: string) => void;
}

enum ModalType {
  None = 'none',
  Elect = 'elect',
  InitiateMigration = 'initiateMigration',
  CancelMigration = 'cancelMigration',
}

const OtherResponsiblePartiesSection: FC<
  OtherResponsiblePartiesSectionProps
> = ({
  variant = 'csp',
  loading = false,
  provisional = false,
  campaign,
  nominationInfo,
  electCnpOptions,
  migrationCnpOptions = [],
  reseller,
  onCnpChange,
  onMigrationApply,
  onMigrationCancel,
  onResellerChange,
}) => {
  const { hasPermission } = usePermission();
  const [campaignActionEligibility, setCampaignActionEligibility] =
    useState<CampaignActionEligibility>();
  const [modalType, setModalType] = useState(ModalType.None);
  const showElect = useMemo(() => {
    if (provisional || campaign.status === CampaignStatus.Expired) {
      return false;
    }
    if (nominationInfo.status) {
      return (
        nominationInfo.status === CampaignSharingStatus.Pending ||
        nominationInfo.status === CampaignSharingStatus.Declined
      );
    }
    return true;
  }, [campaign, nominationInfo]);
  const electLabel =
    nominationInfo.status === CampaignSharingStatus.Pending
      ? 'Change CNP'
      : 'Elect CNP';
  const electModalTitle =
    nominationInfo.status === CampaignSharingStatus.Pending
      ? 'Change Connectivity Partner'
      : 'Elect Connectivity Partner';
  const showInitiateMigration =
    campaign.status === CampaignStatus.Expired
      ? false
      : !!campaignActionEligibility?.startMigration;
  const showCancelMigration =
    campaign.status === CampaignStatus.Expired
      ? false
      : !!campaignActionEligibility?.cancelMigration;
  const electPermission = hasPermission('campaignDetail.electCnp');
  const migrationPermission = hasPermission('campaignDetail.migrateCnp');

  const updateCampaignActionEligibility = async () => {
    const response = await cnpChainController.getCampaignActionEligibility([
      campaign.uid,
    ]);

    if (response && response[campaign.uid]) {
      setCampaignActionEligibility(response[campaign.uid]);
    }
  };

  useEffect(() => {
    if (campaign.status !== CampaignStatus.Expired) {
      updateCampaignActionEligibility();
    }
  }, [campaign.status, nominationInfo.upstreamCnpUid]);

  return (
    <Grid item xs={12}>
      <div className="other-responsible-parties details">
        <SectionHeader
          style={{ justifyContent: 'space-between', marginBottom: 0 }}
          icon={faArrowProgress}
          title="Other Responsible Parties"
        >
          <Flex sx={{ gap: 'xs' }}>
            {showElect && (
              <Button
                className="elect-cnp-btn"
                color="secondary"
                shape="square"
                size="small"
                disabled={loading || !electPermission}
                onClick={() => setModalType(ModalType.Elect)}
              >
                {electLabel}
              </Button>
            )}
            {showInitiateMigration && (
              <Button
                data-testid="initiateMigrationButton"
                shape="square"
                color="secondary"
                size="small"
                disabled={loading || !migrationPermission}
                onClick={() => setModalType(ModalType.InitiateMigration)}
              >
                Initiate Migration
              </Button>
            )}
            {showCancelMigration && (
              <Button
                shape="square"
                color="secondary"
                size="small"
                data-testid="cancelMigrationButton"
                disabled={loading || !migrationPermission}
                onClick={() => setModalType(ModalType.CancelMigration)}
              >
                Cancel Migration
              </Button>
            )}
          </Flex>
        </SectionHeader>
        <CNPDetails variant={variant} nominationInfo={nominationInfo} />
        {variant === 'csp' && (
          <ReSellerDetails
            loader={loading}
            campaignStatus={campaign.status}
            resellerInfo={reseller}
            handleAddReseller={onResellerChange}
          />
        )}
      </div>
      <ElectCNPDialog
        variant={variant}
        mode={
          nominationInfo.status === CampaignSharingStatus.Pending
            ? 'change'
            : 'elect'
        }
        open={modalType === ModalType.Elect}
        title={electModalTitle}
        campaignUid={campaign.uid}
        currentUpstreamCnpUid={nominationInfo.upstreamCnpUid}
        cnpOptions={electCnpOptions}
        handleClose={(_: string, success: boolean) => {
          onCnpChange(success);
          setModalType(ModalType.None);
        }}
      />
      <InitiateMigrationModal
        open={modalType === ModalType.InitiateMigration}
        options={migrationCnpOptions}
        onClose={() => setModalType(ModalType.None)}
        onSubmit={(upstreamCnpUid) => {
          setModalType(ModalType.None);
          onMigrationApply?.(upstreamCnpUid);
        }}
      />
      <CancelMigrationModal
        open={modalType === ModalType.CancelMigration}
        campaignUid={campaign.uid}
        onClose={() => setModalType(ModalType.None)}
        onSubmit={(explanation) => {
          setModalType(ModalType.None);
          onMigrationCancel?.(explanation);
        }}
      />
    </Grid>
  );
};

export default OtherResponsiblePartiesSection;
